<template>
  <div>
    <section :style="{ backgroundImage: hero }" class="-mt-20 pt-40 pb-24 bg-cover bg-center relative">
      <div class="absolute inset-0 h-full w-full bg-header/[var(--bookapp-opacity-hero)]" />
      <BContainer>
        <div class="w-full flex flex-col items-center justify-center">
          <div class="mb-16">
            <img
              v-if="$site.images.logo"
              :src="$site.images.logo.medium"
              class="max-h-[140px] sm:max-h-[170px]"
              alt="Logo"
            >
            <div
              v-else
              class="text-4xl text-secondary font-semibold text-center"
            >
              {{ businessName }}
            </div>
          </div>

          <UButton v-if="$site.tabs.book" to="/book-now" size="lg">
            {{ $site.tabs.book.name }}
          </UButton>
        </div>
      </BContainer>
    </section>

    <section v-if="$site.tabs.isEmpty" class="py-20">
      <BContainer>
        <h2 class="text-primary text-xl font-bold text-center">
          {{ $t('pages.index.notSetUpTitle') }}
        </h2>
        <div class="text-center">
          {{ $t('pages.index.notSetUpContent', { location: businessName }) }}
        </div>
      </BContainer>
    </section>

    <section v-if="$site.settings.notice.enabled" class="py-12">
      <BContainer>
        <b-alert :icon="false" :truncate="isAppLayout">
          <span v-html="formatHtml($site.settings.notice.content)" />
        </b-alert>
      </BContainer>
    </section>

    <section v-if="$site.tabs.contact" class="py-10">
      <BContainer>
        <h2 class="text-primary text-2xl font-bold text-center mb-8">
          {{ $t('pages.index.whereToFindUs') }}
        </h2>
        <div class="sm:flex items-start sm:space-x-6  rtl:space-x-reverse">
          <div v-if="$site.tabs.contact.address.showMap" class="w-full sm:w-1/2 mb-4 sm:mb-0">
            <component
              :is="$site.tabs.contact.address.showMap && $site.tabs.contact.address.displayAddress ? 'a' : 'div'"
              class="block"
              target="_blank"
              :href="$format.maps($site.tabs.contact.address.displayAddress)"
            >
              <img :src="mapUrl" class="w-full rounded overflow-hidden" />
            </component>
          </div>
          <div
            class="w-full max-w-sm"
            :class="$site.tabs.contact.address.showMap ? 'sm:w-1/2' : 'text-center mx-auto'"
          >
            <h4 class="text-secondary text-lg font-bold mb-2">
              {{ $site.tabs.contact.contact.business }}
            </h4>

            <template v-if="$site.tabs.contact.hours.enabled && $site.tabs.contact.businessHours.length > 0">
              <LayoutElementsBusinessHours class="mb-2" />
            </template>

            <LayoutElementsContactLink
              v-if="$site.tabs.contact.contact.email"
              :href="'mailto:' + $site.tabs.contact.contact.email"
              :label="$site.tabs.contact.contact.email"
              icon="i-heroicons-envelope"
            />
            
            <LayoutElementsContactLink
              v-if="$site.tabs.contact.contact.phone"
              :href="'tel:' + stripPhoneNumber($site.tabs.contact.contact.phone)"
              :label="$site.tabs.contact.contact.phone"
              icon="i-heroicons-phone"
            />
            
            <LayoutElementsContactLink
              v-if="$site.tabs.contact.contact.website"
              :href="$format.link($site.tabs.contact.contact.website)"
              :label="$site.tabs.contact.contact.website"
              icon="i-heroicons-globe-alt"
            />
            
            <LayoutElementsContactLink
              v-if="$site.tabs.contact.address.displayAddress"
              :href="$format.maps($site.tabs.contact.address.displayAddress)"
              :label="$site.tabs.contact.address.displayAddress"
              icon="i-heroicons-map-pin"
            />

            <template v-if="$site.tabs.contact.hours.enabled && !$site.tabs.contact.businessHours.length">
              <LayoutElementsBusinessHours class="mb-2" />
            </template>

            <div class="mt-6 ml-10">
              <LayoutElementsSocialLinks :social-links="$site.settings.socialLinks" :class="$site.tabs.contact.address.showMap ? '' : 'justify-center'" />
            </div>
          </div>
        </div>
      </BContainer>
    </section>

    <section v-if="$site.tabs.timetable" class="py-16">
      <BContainer>
        <h2 class="text-primary text-2xl font-bold text-center mb-8">
          {{ $site.tabs.timetable.name }}: {{ $t('pages.index.today') }}
        </h2>
        <div>
          <div v-if="timetable.loading" class="text-center">
            <BSpinner label="Loading" />
          </div>

          <BError v-if="timetable.error" :name="$t('pages.index.timetable')" />

          <template v-if="timetable.data">
            <div v-if="!timetable.data.length" class="m-3 text-center text-sm text-secondary">
              {{ $t('pages.index.noData') }}
            </div>
            <ul v-if="(timetable.data && timetable.data.length)" class="home-timetable-list">
              <li v-for="slot in timetable.data" :key="slot.data">
                {{ slot.startTimeLabel }}:
                <a v-if="slot.available" class="text-primary" @click="timetableSlotClicked(slot)">
                  {{ $t('general.serviceWithEmployee',{service: slot.services[0].service.title, employee: slot.services[0].employee.firstName}) }}
                </a>
                <span v-else>
                  {{ $t('general.serviceWithEmployee',{service: slot.services[0].service.title, employee: slot.services[0].employee.firstName}) }}
                </span>

                <small v-if="$site.settings.bookShowServicePrices && slot.totalPrice > 0">
                  - {{ slot.totalPriceLabel }}
                </small>

                <b-chip v-if="slot.classStatus === 'almost-full'" variant="warning">
                  {{ $t('general.almostFull') }}
                </b-chip>
                <b-chip v-if="slot.classStatus === 'full'" variant="secondary">
                  {{ $t('pages.index.full') }}
                </b-chip>
              </li>
            </ul>

            <div class="flex items-center justify-center">
              <UButton class="mt-4 mx-auto block" :to="{ path: '/timetable' }">
                {{ $t('general.seeMore') }} »
              </UButton>
            </div>
          </template>
        </div>
      </BContainer>
    </section>

    <section v-if="$site.tabs.review" class="py-16">
      <BContainer>
        <div class="flex items-center justify-center mb-8">
          <h2 class="text-primary text-center text-2xl font-bold">
            {{ $site.tabs.review.name }}
          </h2>

          <b-chip class="ml-3">
            <span>{{ $site.tabs.review.stats.count }}</span>
          </b-chip>
        </div>
        <div>
          <div v-if="$site.tabs.review.latest && $site.tabs.review.latest.length" class="grid sm:grid-cols-4 gap-4">
            <ReviewElementCard
              v-for="(review, index) in $site.tabs.review.latest.slice(0, 3)"
              :key="index"
              :review="review"
            />
          </div>

          <div class="flex items-center justify-center">
            <UButton class="mt-4 mx-auto block" :to="{ path: '/reviews' }">
              {{ $t('pages.index.showAll') }} »
            </UButton>
          </div>
        </div>
      </BContainer>
    </section>
  </div>
</template>

<script setup lang="ts">
import Utils from '@/utils'

import { BookingStage, createBookingRoute } from '@/components/booking/elements/Detail.vue'

const { $site, $store, $api } = useNuxtApp()
const locationStore = useLocationStore()

locationStore.getServices()

const timetable = ref({
  loading: false,
  error: false,
  data: null
})

const hero = computed(() => {
  return $site.images.heroImageCssUrl('large')
})

const mapUrl = computed(() => {
  return $site.location.mapUrl({ size: '450x300', zoom: 15 })
})

const businessName = computed(() => {
  if ($site.tabs.contact && $site.tabs.contact.business) {
    return $site.tabs.contact.business
  } else {
    return $site.location.title
  }
})

const isAppLayout = computed(() => {
  return $site.layout === 'app'
})

onMounted(() => {
  if ($site.tabs.timetable) {
    loadTimetable()
  }
})

const formatHtml = (text: string) => {
  return Utils.text.formatHtml(text)
}

const stripPhoneNumber = (text: string) => {
  return Utils.text.stripPhoneNumber(text)
}

const loadTimetable = async () => {
  timetable.value.loading = true

  try {
    const response = await $api.booking.timetableDay(new Date())
    timetable.value.data = response.slots
  } catch (error) {
    // console.log(error)
    
    timetable.value.loading = false
    timetable.value.error = true
  }

  timetable.value.loading = false
}

const timetableSlotClicked = (slot) => {
  $store.dispatch('booking/clearServiceSelections')

  for (let i = 0; i < slot.services.length; i++) {
    const slotService = slot.services[i]

    $store.dispatch('booking/setServiceSelected', {
      service: slotService.service,
      selected: true,
      toBack: true
    })
  }

  $store.dispatch('booking/setSelectedSlot', {
    slot,
    mode: 'timetable'
  })

  const bookingUrlQuery = $store?.getters['booking/urlQuery']

  if ($site.settings.showGroupDetail) {
    navigateTo(
      createBookingRoute(bookingUrlQuery, BookingStage.DETAIL)
    )
  } else {
    navigateTo(
      createBookingRoute(bookingUrlQuery, BookingStage.CUSTOMER)
    )
  }
}

const triggerError = () => {
    throw new Error("Nuxt Button Error");
  };
</script>

<style>
.home-timetable-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.home-timetable-list li {
  margin-bottom: 10px;
  text-align: center !important;
}

.home-timetable-list li a {
  text-align: left !important;
  font-size: 14px;
}

.map {
  width: 100%;
  height: 100%;
  min-height: 250px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: 50% 50%;
}

.hero-section {
  background-position: center;
  background-size: cover;
  position: relative;
  background-image: var(--bookapp-hero-background-image-small);
  color: white;
}

.hero-section > .container {
  display: block;
  position: relative;
}

.hero-section .scroll-indicator svg {
  font-size: 1.5rem;
}

@media (min-width: 768px) {
  .hero-section {
    background-image: var(--bookapp-hero-background-image-medium);
  }
}

@media (min-width: 1200px) {
  .hero-section {
    background-image: var(--bookapp-hero-background-image-large);
  }
}

.hero-section:before {
  position: absolute;
  left: 0px;
  content: ' ';
  width: 100%;
  height: 100%;
  display: block;
  background: var(--bookapp-hero-background-color);
  opacity: var(--bookapp-hero-background-opacity);
}

.hero-logo {
  max-width: 100%;
  max-height: 200px;
  font-size: 3rem;
}

.scroll-indicator {
  -webkit-animation-name: bounce2;
  animation-name: bounce2;
  -webkit-animation-duration: 1.5s;
  animation-duration: 1.5s;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
  -webkit-animation-timing-function: linear;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  -webkit-animation-iteration-count: infinite;
}
@-webkit-keyframes bounce2 {
  0%,
  100% {
    -webkit-transform: translateY(0);
  }
  50% {
    -webkit-transform: translateY(5px);
  }
}
@keyframes bounce2 {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(5px);
  }
}
</style>
